import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    
    // Setup comprehensive cart change listeners
    this.#setupCartChangeListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    console.log('🛒 Cart add event detected');
    
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
    
    // Dispatch cart updated event for progress bar
    this.#dispatchCartUpdated('item added');
  };

  open() {
    console.log('🛒 Cart drawer opening');
    this.showDialog();
    
    // Update progress bar when drawer opens
    this.#dispatchCartUpdated('drawer opened');

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    console.log('🛒 Cart drawer closing');
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  /**
   * Set up comprehensive listeners for all cart changes
   */
  #setupCartChangeListeners() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) {
      console.warn('⚠️ Cart drawer dialog not found, cannot setup listeners');
      return;
    }

    console.log('🔧 Setting up cart change listeners');

    // Listen for quantity input changes with debouncing
    let quantityTimeout;
    dialog.addEventListener('input', (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      if (target.matches('input[name="updates[]"], input[name*="quantity"], .quantity__input, input[type="number"]')) {
        clearTimeout(quantityTimeout);
        quantityTimeout = setTimeout(() => {
          console.log('🛒 Quantity input changed');
          this.#dispatchCartUpdated('quantity changed');
        }, 500);
      }
    });

    // Also listen for change events (when user finishes editing)
    dialog.addEventListener('change', (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      if (target.matches('input[name="updates[]"], input[name*="quantity"], .quantity__input, input[type="number"]')) {
        console.log('🛒 Quantity change confirmed');
        this.#dispatchCartUpdated('quantity confirmed');
      }
    });

    // Listen for item removals and updates
    dialog.addEventListener('click', (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      const removeButton = target.closest('[data-cart-remove], .cart-remove-button, button[name="update"], [href*="/cart/change"]');
      
      if (removeButton) {
        console.log('🛒 Remove/update button clicked');
        // Longer delay for removals to ensure cart is updated
        setTimeout(() => this.#dispatchCartUpdated('item removed'), 600);
      }
    });

    // Listen for form submissions (cart updates)
    dialog.addEventListener('submit', (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      if (target.matches('form[action*="/cart"]')) {
        console.log('🛒 Cart form submitted');
        setTimeout(() => this.#dispatchCartUpdated('form submitted'), 400);
      }
    });

    // Monitor cart items component for DOM changes
    const cartItems = dialog.querySelector('cart-items-component, .cart-items');
    if (cartItems) {
      const observer = new MutationObserver((mutations) => {
        // Only trigger if actual content changed (not just attributes)
        const hasContentChange = mutations.some(m => 
          m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0)
        );
        
        if (hasContentChange) {
          console.log('🛒 Cart items DOM changed');
          this.#dispatchCartUpdated('DOM mutation');
        }
      });
      
      observer.observe(cartItems, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Dispatch a custom event to notify listeners that the cart has been updated
   * @param {string} reason - Description of what triggered the update
   */
  #dispatchCartUpdated(reason = 'unknown') {
    console.log(`📢 Dispatching cart:updated event (reason: ${reason})`);
    
    // Fetch current cart state and dispatch event with cart data
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Dispatch the main event
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart, reason },
          bubbles: true
        }));
        
        // Also dispatch alternative event name for compatibility
        document.dispatchEvent(new CustomEvent('cart:change', {
          detail: { cart, reason },
          bubbles: true
        }));
        
        console.log('✅ Cart events dispatched successfully', {
          items: cart.item_count,
          total: cart.total_price / 100
        });
      })
      .catch(error => {
        console.error('❌ Error fetching cart:', error);
        
        // Still dispatch events even if fetch fails
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { reason, error: true },
          bubbles: true
        }));
        
        document.dispatchEvent(new CustomEvent('cart:change', {
          detail: { reason, error: true },
          bubbles: true
        }));
      });
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
  console.log('✅ cart-drawer-component registered');
}